import { supabase } from '../supabaseClient'
import { offlineDB } from './db'
import { getAuthItem } from '../utils/auth'
import { authHeaders } from '../utils/authFetch'
import { enqueueAction, resolveId, setIdMapping } from './syncQueue'
import { v4 as uuidv4 } from 'uuid'

const payableStatuses = new Set(['active', 'void_rejected'])

// Staff context (needed to build orders, must work offline) 
export async function getStaffContext(isOnline) {
    if (isOnline) {
        const userEmail = getAuthItem('userEmail')
        const { data, error } = await supabase
            .from('staff')
            .select('id, branch_id, role, email')
            .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
            .single()
        if (error) throw error
        await offlineDB.metaCache.put({ key: 'staffContext', value: data })
        return data
    }
    const cached = await offlineDB.metaCache.get('staffContext')
    if (!cached) throw new Error('Staff info not cached yet. Please reconnect once before going offline.')
    return cached.value
}

// try {
//     const { data: branchItemsRaw } = await supabase
//         .from('branch_menu_items')
//         .select(`item_id, branch_price, menu_items ( item_id, name, item_price, category, classification, item_img, is_archived )`)
//         .eq('branch_id', staffData.branch_id)

//     allBranchItems = (branchItemsRaw || [])
//         .map(row => row.menu_items && !row.menu_items.is_archived
//             ? { ...row.menu_items, item_price: row.branch_price ?? row.menu_items.item_price }
//             : null)
//         .filter(Boolean)

//     await refreshOfflineCaches(staffData.branch_id, allBranchItems)

//     const itemIds = allBranchItems.map(i => i.item_id)
//     const recipes = await offlineDB.recipeCache.where('item_id').anyOf(itemIds).toArray()
//     const inventoryRows = await offlineDB.inventoryCache.toArray()
//     const availableMap = new Map(inventoryRows.map(r => [r.ingredient_id, r.available]))

//     allBranchItems = allBranchItems.map(item => {
//         const itemRecipes = recipes.filter(r => r.item_id === item.item_id)
//         const isAvailable = itemRecipes.length > 0 && itemRecipes.every(r => {
//             const avail = availableMap.get(r.ingredient_id)
//             return avail !== undefined && avail >= Number(r.quantity_used || 0)
//         })
//         return { ...item, isAvailable }
//     })

//     await offlineDB.menuCache.bulkPut(allBranchItems.map(i => ({ ...i, branch_id: staffData.branch_id })))
// } catch (err) {
//     console.error('Menu/inventory prefetch failed:', err)
// }

// ── Refresh all offline caches (call whenever online + branch known) ──
export async function refreshOfflineCaches(branchId, allBranchItems) {
    if (!branchId) return
    // Branch address + cashier fallback name, used on offline receipts
    const { data: branchData } = await supabase
        .from('branches').select('address').eq('branch_id', branchId).single()
    await offlineDB.metaCache.put({ key: 'branchAddress', value: branchData?.address || null })

    // Inventory snapshot: current_stock - active reservations
    const { data: inventory } = await supabase
        .schema('inventory').from('branch_inventory')
        .select('ingredient_id, current_stock').eq('branch_id', branchId)

    const { data: reservations } = await supabase
        .schema('inventory').from('stock_reservations')
        .select('ingredient_id, reserved_quantity')
        .eq('branch_id', branchId).eq('status', 'reserved')

    const reservedMap = new Map()
    for (const r of reservations || []) {
        reservedMap.set(r.ingredient_id, (reservedMap.get(r.ingredient_id) || 0) + Number(r.reserved_quantity || 0))
    }

    const inventoryRows = (inventory || []).map(inv => ({
        ingredient_id: inv.ingredient_id,
        available: Number(inv.current_stock || 0) - (reservedMap.get(inv.ingredient_id) || 0),
    }))
    await offlineDB.inventoryCache.clear()
    await offlineDB.inventoryCache.bulkPut(inventoryRows)

    // Recipes + variant rules for every item in the branch
    const itemIds = allBranchItems.map(i => i.item_id)
    if (itemIds.length > 0) {
        const { data: recipes } = await supabase
            .schema('inventory').from('menu_item_ingredients')
            .select('item_id, ingredient_id, quantity_used, unit').in('item_id', itemIds)
        await offlineDB.recipeCache.clear()
        await offlineDB.recipeCache.bulkPut(recipes || [])

        const { data: variantRules } = await supabase
            .schema('inventory').from('menu_item_variant_ingredient_rules')
            .select('item_id, variant_id, ingredient_id, quantity_delta, unit').in('item_id', itemIds)
        await offlineDB.variantRuleCache.clear()
        await offlineDB.variantRuleCache.bulkPut(variantRules || [])
    }

    // Variants + modifiers for every item in the branch (for offline customization modal)
    if (itemIds.length > 0) {
        const { data: menuVariantsData } = await supabase
            .from('menu_variants')
            .select(`item_id, item_variants (variant_id, variant_name, category, price, is_archived)`)
            .in('item_id', itemIds)

        const variantRows = (menuVariantsData || [])
            .map(row => row.item_variants && !row.item_variants.is_archived
                ? { item_id: row.item_id, ...row.item_variants }
                : null)
            .filter(Boolean)
        await offlineDB.variantCache.clear()
        await offlineDB.variantCache.bulkPut(variantRows)

        const { data: menuModifiersData } = await supabase
            .from('menu_modifiers')
            .select(`item_id, item_modifiers (modifier_id, modifier_name, category, price, is_archived)`)
            .in('item_id', itemIds)

        const modifierRows = (menuModifiersData || [])
            .map(row => row.item_modifiers && !row.item_modifiers.is_archived
                ? { item_id: row.item_id, ...row.item_modifiers }
                : null)
            .filter(Boolean)
        await offlineDB.modifierCache.clear()
        await offlineDB.modifierCache.bulkPut(modifierRows)
    }

    // Customer/loyalty snapshot for offline duplicate-checking and lookup
    const { data: loyaltyRows } = await supabase
        .from('loyalty_accounts')
        .select('loyalty_id, membership_number, points_balance, is_active, customers ( user_id, email, first_name, last_name )')
        .eq('is_active', true)

    await offlineDB.customerLoyaltyCache.clear()
    await offlineDB.customerLoyaltyCache.bulkPut(
        (loyaltyRows || [])
            .filter(r => r.customers)
            .map(r => ({
                email: r.customers.email,
                user_id: r.customers.user_id,
                loyalty_id: r.loyalty_id,
                membership_number: r.membership_number,
                points_balance: Number(r.points_balance || 0),
                name: `${r.customers.first_name} ${r.customers.last_name}`,
            }))
    )

    // const { data: rewards, error: rewardsError } = await supabase
    //     .schema('marketing').from('rewards')
    //     .select(`reward_id, reward_name, description, reward_value_type, reward_value,
    //         max_discount_amount, application_scope, applies_to_category, minimum_spend,
    //         reward_rules ( rule_type, target_value ), reward_branches!inner ( branch_id ),
    //         reward_items ( item_id, variant_id )`)
    //     .in('reward_id', rewardIds).eq('is_active', true).eq('is_archived', false)
    //     .eq('reward_branches.branch_id', branchId)
    // if (rewardsError) console.error('[refreshOfflineCaches] rewards fetch failed:', rewardsError.message)
        
        
    const nowIso = new Date().toISOString()
    const { data: campaigns, error: campaignsError } = await supabase
        .schema('marketing').from('promotions')
        .select(`promotion_id, promotion_name, linked_reward_id, promotion_branches!inner ( branch_id )`)
        .eq('status', 'active').lte('start_at', nowIso).gte('end_at', nowIso)
        .eq('promotion_branches.branch_id', branchId).not('linked_reward_id', 'is', null)

    if (campaignsError) console.error('[refreshOfflineCaches] campaigns fetch failed:', campaignsError.message)

    const rewardIds = [...new Set((campaigns || []).map(c => c.linked_reward_id))]
    await offlineDB.rewardsCache.where('branch_id').equals(branchId).delete()

    if (rewardIds.length > 0) {
        const { data: rewards, error: rewardsError } = await supabase
            .schema('marketing').from('rewards')
            .select(`reward_id, reward_name, description, reward_value_type, reward_value,
                max_discount_amount, application_scope, applies_to_category, minimum_spend,
                reward_rules ( rule_type, target_value ), reward_branches!inner ( branch_id ),
                reward_items ( item_id, variant_id )`)
            .in('reward_id', rewardIds).eq('is_active', true).eq('is_archived', false)
            .eq('reward_branches.branch_id', branchId)

        if (rewardsError) console.error('[refreshOfflineCaches] rewards fetch failed:', rewardsError.message)

        await offlineDB.rewardsCache.bulkPut((rewards || []).map(r => {
            const c = campaigns.find(x => x.linked_reward_id === r.reward_id)
            return {
                reward_id: r.reward_id, branch_id: branchId,
                rewardName: r.reward_name, description: r.description,
                campaignId: c?.promotion_id, campaignName: c?.promotion_name,
                rewardValueType: r.reward_value_type, rewardValue: r.reward_value,
                maxDiscountAmount: r.max_discount_amount, applicationScope: r.application_scope,
                appliesToCategory: r.applies_to_category, minimumSpend: r.minimum_spend || 0,
                eligibleItems: (r.reward_items || []).map(i => ({ item_id: i.item_id, variant_id: i.variant_id })),
                rules: (r.reward_rules || []).map(rr => ({ rule_type: rr.rule_type, target_value: rr.target_value })),
            }
        }))
    }

    // A fresh online snapshot supersedes whatever offline orders had
    // already been counted against the old numbers.
    await offlineDB.offlineReserved.clear()
}

// Approximate eligibility offline — order-count-based rules can't be
// verified without a live query, so they're passed through and the
// server re-validates for real when the redemption syncs.
export async function getEligibleVouchersOffline(loyaltyCustomer, branchId) {
    const cachedRewards = await offlineDB.rewardsCache.where('branch_id').equals(branchId).toArray()
    const usedThisOutage = await offlineDB.offlineRedemptions.where('loyaltyKey').equals(loyaltyCustomer.loyaltyId).toArray()

    return cachedRewards.map(reward => {
        let blocked = null
        for (const rule of reward.rules || []) {
            if (rule.rule_type === 'minimum_points' && Number(loyaltyCustomer.pointsBalance || 0) < rule.target_value) {
                blocked = `Needs ${rule.target_value} pts`
            } else if (rule.rule_type === 'single_redemption' && usedThisOutage.some(r => r.reward_id === reward.reward_id)) {
                blocked = 'Already redeemed'
            }
            if (blocked) break
        }
        return { ...reward, eligible: !blocked, ineligibleReason: blocked }
    })
}

// Call this ONCE, right after staff login resolves branchId.
// It warms every cache the POS / Orders / Rewards screens rely on.
export async function warmStaffSession(isOnline) {
    if (!isOnline) return

    const staffData = await getStaffContext(true)

    //  try {
    //     const debugCtx = await getBranchStaffContext(true)
    //     console.log('[warmStaffSession] getStaffContext branch:', staffData.branch_id, 'vs getBranchStaffContext branch:', debugCtx.branch_id)
    // } catch (e) { console.log('[warmStaffSession] getBranchStaffContext debug failed:', e.message) }
    

    if (!staffData?.branch_id) return

    // ── PRIORITY: resolve branch context + cache PINs FIRST ──────────
    // These are small, fast calls and are what offline void/cancel actually
    // depends on. Do them before the slow menu/inventory/orders prefetch
    // below, so they're guaranteed to finish even if the connection drops
    // moments after login (the realistic offline-testing scenario).
    let branchCtxId = null
    try {
        const branchCtx = await getBranchStaffContext(true)
        branchCtxId = branchCtx.branch_id
    } catch (err) {
        console.error('[warmStaffSession] branch context resolve failed:', err.message)
    }

    if (branchCtxId) {
        try {
            await cacheAdminPinsForBranch(branchCtxId)
        } catch (err) {
            console.error('Admin PIN cache prefetch failed:', err)
        }
        try {
            await cacheBranchBaristasAndSchedules(branchCtxId)
        } catch (err) {
            console.error('Barista cache prefetch failed:', err)
        }
        try {
            await cacheLoyaltyCatalog()
        } catch (err) {
            console.error('Loyalty catalog cache prefetch failed:', err)
        }
    }
    // ───────────────────────────────────────────────────────────────

    let allBranchItems = []


    // 1. Menu + inventory + variants/modifiers
    try {
    const branchCtx = await getBranchStaffContext(true)
    const branchId = branchCtx.branch_id

    const { data: branchItemsRaw } = await supabase
        .from('branch_menu_items')
        .select(`item_id, branch_price, menu_items ( item_id, name, item_price, category, classification, item_img, is_archived )`)
        .eq('branch_id', branchId)

    allBranchItems = (branchItemsRaw || [])
        .map(row => row.menu_items && !row.menu_items.is_archived
            ? { ...row.menu_items, item_price: row.branch_price ?? row.menu_items.item_price }
            : null)
        .filter(Boolean)

    await refreshOfflineCaches(branchId, allBranchItems)

    const itemIds = allBranchItems.map(i => i.item_id)
        const recipes = await offlineDB.recipeCache.where('item_id').anyOf(itemIds).toArray()
        const inventoryRows = await offlineDB.inventoryCache.toArray()
        const availableMap = new Map(inventoryRows.map(r => [r.ingredient_id, r.available]))

        allBranchItems = allBranchItems.map(item => {
            const itemRecipes = recipes.filter(r => r.item_id === item.item_id)
            const isAvailable = itemRecipes.length > 0 && itemRecipes.every(r => {
                const avail = availableMap.get(r.ingredient_id)
                return avail !== undefined && avail >= Number(r.quantity_used || 0)
            })
            return { ...item, isAvailable }
        })

        await offlineDB.menuCache.bulkPut(allBranchItems.map(i => ({ ...i, branch_id: branchId })))
    
    } catch (err) {
        console.error('Menu/inventory prefetch failed:', err)
    }

    // 1b. Inventory Management screen data (stock list + recipe reference)
    // NOTE: use getBranchStaffContext's branch_id here — that's the same
    // resolver InventoryManagement.js and OrderManagement.js call when they
    // read this cache. Using a different resolver (getStaffContext) risks
    // caching under a different branch_id than what those screens look up.
    try {
        const branchCtx = await getBranchStaffContext(true)
        const invBranchId = branchCtx.branch_id

        const invApi = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/inventory`

        const [stockRes, recipeRes, menuItemsRes] = await Promise.all([
            fetch(`${invApi}/${invBranchId}/stock`, { headers: authHeaders() }),
            fetch(`${invApi}/${invBranchId}/recipes`, { headers: authHeaders() }),
            fetch(`${invApi}/${invBranchId}/menu-items`, { headers: authHeaders() }),
        ])

        if (stockRes.ok) {
            const stockList = await stockRes.json()
            await offlineDB.invStockListCache.where('branch_id').equals(invBranchId).delete()
            await offlineDB.invStockListCache.bulkPut(stockList.map(item => ({ ...item, branch_id: invBranchId })))
            console.log('[warmStaffSession] cached', stockList.length, 'stock rows for branch', invBranchId)
        }

        if (recipeRes.ok) {
            const recipeList = await recipeRes.json()
            await offlineDB.invRecipeRefCache.where('branch_id').equals(invBranchId).delete()
            await offlineDB.invRecipeRefCache.bulkPut(recipeList.map(item => ({ ...item, branch_id: invBranchId })))
        }

        if (menuItemsRes.ok) {
            const menuItemsList = await menuItemsRes.json()
            await offlineDB.invMenuItemsCache.where('branch_id').equals(invBranchId).delete()
            await offlineDB.invMenuItemsCache.bulkPut(menuItemsList.map(item => ({ ...item, branch_id: invBranchId })))
        }
        
    } catch (err) {
        console.error('[warmStaffSession] stock prefetch failed:', err)
    }

    // 2. Orders
    try {
        const { data: orders } = await supabase
            .from('orders')
            .select(`order_id, client_ref, reference_number, receipt_number, branch_id, order_source, fulfillment_type,
                      order_status, subtotal, discount, tax, final_amount, created_at, updated_at, completed_at,
                      barista_id, order_completed_by`)
            .eq('branch_id', staffData.branch_id)
            .order('created_at', { ascending: false })

        if (orders?.length) {
            await offlineDB.ordersCache.clear()
            await offlineDB.ordersCache.bulkPut(orders)
            await cacheOrderItemsForOrders(orders.map(o => o.order_id))

            const highestReceipt = orders
                .filter(o => o.receipt_number)
                .sort((a, b) => {
                    const an = parseInt(String(a.receipt_number).match(/\d+/)?.[0] || '0', 10)
                    const bn = parseInt(String(b.receipt_number).match(/\d+/)?.[0] || '0', 10)
                    return bn - an
                })[0]?.receipt_number

            if (highestReceipt) {
                await setLastKnownReceiptNumber(staffData.branch_id, highestReceipt)
            }
        }
    } catch (err) {
        console.error('Orders prefetch failed:', err)
    }

    // 3. Branch staff context
    try {
        await getBranchStaffContext(true)
    } catch (err) {
        console.error('Branch staff context prefetch failed:', err)
    }

    // 4. Staff profile + display name — display name is now the branch name,
    // not the staff record's first/last name (those no longer identify the
    // logged-in account; see BRANCHES.branch_owner migration).
    try {
        const userEmail = getAuthItem('userEmail')
        const { data: staffRow } = await supabase
            .from('staff')
            .select('id, email, branch_id, branches ( name )')
            .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
            .single()

        if (staffRow) {
            await offlineDB.metaCache.put({
                key: 'staffProfile',
                value: { id: staffRow.id, branch_id: staffRow.branch_id, branch_name: staffRow.branches?.name || 'Assigned Branch' }
            })

            const { data: profileData } = await supabase
                .from('profiles')
                .select('profile_picture_url')
                .eq('user_id', staffRow.id)
                .eq('user_type', 'staff')
                .maybeSingle()

            await offlineDB.metaCache.put({
                key: 'staffDisplayInfo',
                value: {
                    name: staffRow.branches?.name || 'Assigned Branch',
                    profilePicture: profileData?.profile_picture_url || null
                }
            })
        }
    } catch (err) {
        console.error('Staff profile prefetch failed:', err)
    }

    // Resolve once, reused by steps 5 & 6 — must match the resolver that
    // POSLayout / OrderManagement actually read staffBranchId from
    // (getBranchStaffContext), not getStaffContext. See step 1b note above;
    // caching under the wrong branch_id here means adminPinCache.get(staffBranchId)
    // silently misses and offline void/cancel PIN checks fail.
    // let branchCtxId = null
    // try {
    //     const branchCtx = await getBranchStaffContext(true)
    //     branchCtxId = branchCtx.branch_id
    // } catch (err) {
    //     console.error('[warmStaffSession] branch context resolve failed:', err.message)
    // }

    // // 5. Barista roster + PIN hashes + schedules + this month's attendance
    // if (branchCtxId) {
    //     try {
    //         await cacheBranchBaristasAndSchedules(branchCtxId)
    //     } catch (err) {
    //         console.error('Barista cache prefetch failed:', err)
    //     }
    // }

    // // 6. Branch Admin PIN hashes — needed offline for void-item / cancel-order
    // // authorization, mirroring how barista PINs are pre-cached above.
    // if (branchCtxId) {
    //     try {
    //         await cacheAdminPinsForBranch(branchCtxId)
    //     } catch (err) {
    //         console.error('Admin PIN cache prefetch failed:', err)
    //     }
    // }
}

// Offline stock validation (mirrors validateStockAvailability) 
export async function validateStockOffline(cartItems) {
    const itemIds = [...new Set(cartItems.map(i => i.item_id))]
    const recipes = await offlineDB.recipeCache.where('item_id').anyOf(itemIds).toArray()

    const allVariantIds = cartItems.flatMap(item => (item.variantObjects || []).map(v => v.variant_id))
    const variantRules = allVariantIds.length
        ? await offlineDB.variantRuleCache.where('item_id').anyOf(itemIds).toArray()
        : []

    const inventoryRows = await offlineDB.inventoryCache.toArray()
    const availableMap = new Map(inventoryRows.map(r => [r.ingredient_id, r.available]))

    const offlineReservedRows = await offlineDB.offlineReserved.toArray()
    const consumedMap = new Map(offlineReservedRows.map(r => [r.ingredient_id, r.qty]))

    const requiredMap = new Map()
    for (const cartItem of cartItems) {
        if (!payableStatuses.has(cartItem.line_status || 'active')) continue
        const qty = parseInt(cartItem.qty, 10) || 0
        if (qty <= 0) continue

        for (const r of recipes.filter(r => r.item_id === cartItem.item_id)) {
            const needed = Number(r.quantity_used || 0) * qty
            requiredMap.set(r.ingredient_id, (requiredMap.get(r.ingredient_id) || 0) + needed)
        }

        const selectedVariantIds = (cartItem.variantObjects || []).map(v => v.variant_id)
        if (selectedVariantIds.length > 0) {
            for (const rule of variantRules.filter(
                r => r.item_id === cartItem.item_id && selectedVariantIds.includes(r.variant_id)
            )) {
                const delta = Number(rule.quantity_delta || 0) * qty
                requiredMap.set(rule.ingredient_id, (requiredMap.get(rule.ingredient_id) || 0) + delta)
            }
        }
    }

    const insufficient = []
    for (const [ingredientId, requiredQty] of requiredMap.entries()) {
        if (requiredQty <= 0) continue
        const cachedAvailable = availableMap.get(ingredientId)
        if (cachedAvailable === undefined) {
            insufficient.push({ ingredient_id: ingredientId, required: requiredQty, available: 0, reason: 'not_tracked' })
            continue
        }
        const effectiveAvailable = cachedAvailable - (consumedMap.get(ingredientId) || 0)
        if (requiredQty > effectiveAvailable) {
            insufficient.push({ ingredient_id: ingredientId, required: requiredQty, available: effectiveAvailable })
        }
    }

    return { insufficient, requiredMap }
}

// Call this only after an offline order is confirmed, so the NEXT offline
// order in the same outage sees reduced availability.
export async function commitOfflineConsumption(requiredMap) {
    for (const [ingredientId, qty] of requiredMap.entries()) {
        const existing = await offlineDB.offlineReserved.get(ingredientId)
        await offlineDB.offlineReserved.put({ ingredient_id: ingredientId, qty: (existing?.qty || 0) + qty })
    }
}

//Build reservation rows from cached recipe/variant data 
export async function buildReservationRowsOffline(cartItem, { branchId, orderId, orderItemId }) {
    const qty = parseInt(cartItem.qty, 10) || 0
    if (qty <= 0) return []

    const baseIngredients = await offlineDB.recipeCache.where('item_id').equals(cartItem.item_id).toArray()
    const grouped = new Map()
    for (const ing of baseIngredients) {
        const key = `${ing.ingredient_id}__${ing.unit || ''}`
        const current = grouped.get(key) || {
            order_id: orderId,
            order_item_id: orderItemId,
            branch_id: branchId,
            ingredient_id: ing.ingredient_id,
            reserved_quantity: 0,
            unit: ing.unit || '',
            status: 'reserved',
        }
        current.reserved_quantity += (parseFloat(ing.quantity_used) || 0) * qty
        grouped.set(key, current)
    }

    const selectedVariantIds = (cartItem.variantObjects || []).map(v => v.variant_id)
    if (selectedVariantIds.length > 0) {
        const rules = (await offlineDB.variantRuleCache.where('item_id').equals(cartItem.item_id).toArray())
            .filter(r => selectedVariantIds.includes(r.variant_id))
        for (const rule of rules) {
            const key = `${rule.ingredient_id}__${rule.unit || ''}`
            const current = grouped.get(key) || { ingredient_id: rule.ingredient_id, reserved_quantity: 0, unit: rule.unit || '' }
            current.reserved_quantity += (parseFloat(rule.quantity_delta) || 0) * qty
            grouped.set(key, current)
        }
    }

    return Array.from(grouped.values()).filter(row => row.reserved_quantity > 0)
}

export async function getBranchStaffContext(isOnline) {
    if (isOnline) {
        const sessionStaffId = getAuthItem('staffId')
        if (!sessionStaffId) throw new Error('Staff session not found.')

        const { data: accountData, error: accountError } = await supabase
            .from('staff_accounts')
            .select('id, staff_id')
            .or(`id.eq.${sessionStaffId},staff_id.eq.${sessionStaffId}`)
            .single()
        if (accountError) throw accountError
        if (!accountData?.staff_id) throw new Error('No linked staff record found for this account.')

        const { data: staffData, error: staffError } = await supabase
            .from('staff')
            .select(`id, role, branch_id, branches ( branch_id, name )`)
            .eq('id', accountData.staff_id)
            .single()
        if (staffError) throw staffError
        if (!staffData?.branch_id) throw new Error('No branch assigned to this staff account.')

        const context = {
            id: staffData.id,
            role: staffData.role || '',
            branch_id: staffData.branch_id,
            branch_name: staffData.branches?.name || 'Assigned Branch',
        }

        await offlineDB.metaCache.put({ key: 'branchStaffContext', value: context })
        return context
    }

    const cached = await offlineDB.metaCache.get('branchStaffContext')
    if (!cached) throw new Error('Staff info not cached yet. Please reconnect once before going offline.')
    return cached.value
}

export async function cacheOrderItemsForOrders(orderIds) {
    if (!orderIds || orderIds.length === 0) return

    // Local (not-yet-synced) order ids poison the .in() filter for the
    // whole chunk they're in — Postgres errors on the invalid id/UUID,
    // and every real order sharing that chunk loses its cached items too.
    const realOrderIds = [...new Set(orderIds)].filter(id => !String(id).startsWith('local_'))
    if (realOrderIds.length === 0) return

    const CHUNK_SIZE = 150
    for (let i = 0; i < realOrderIds.length; i += CHUNK_SIZE) {
        const chunk = realOrderIds.slice(i, i + CHUNK_SIZE)

        const { data, error } = await supabase
            .from('order_items')
            .select(`
                *,
                menu_items ( name, category, item_img, classification ),
                order_item_variants ( variant_id, variant_price, item_variants ( variant_id, variant_name, category ) ),
                order_item_modifiers ( modifier_id, modifier_price, item_modifiers ( modifier_id, modifier_name, category ) )
            `)
            .in('order_id', chunk)

        if (error) {
            console.error('Failed to cache order items for offline use:', error)
            continue
        }

        // Atomic delete+put so a concurrent call to this same function
        // (fired from overlapping fetchOrders() calls) can't clear rows
        // another in-flight call just wrote.
        await offlineDB.transaction('rw', offlineDB.orderItemsCache, async () => {
            await offlineDB.orderItemsCache.where('order_id').anyOf(chunk).delete()
            await offlineDB.orderItemsCache.bulkPut(data || [])
        })
    }
}

export async function cacheOrderTransactionsForOrders(orderIds) {
    if (!orderIds || orderIds.length === 0) return
    const realOrderIds = [...new Set(orderIds)].filter(id => !String(id).startsWith('local_'))
    if (realOrderIds.length === 0) return

    const { data, error } = await supabase
        .from('order_transactions')
        .select('order_id, payment_method, transaction_amount, status')
        .in('order_id', realOrderIds)

    if (error) { console.error('Failed to cache transactions:', error); return }

    const grouped = new Map()
    for (const row of data || []) {
        if (!grouped.has(row.order_id)) grouped.set(row.order_id, [])
        grouped.get(row.order_id).push(row)
    }
    for (const [orderId, transactions] of grouped.entries()) {
        await offlineDB.orderTransactionsCache.put({ order_id: orderId, transactions })
    }
}

export async function checkLoyaltyByEmailOffline(email) {
    const row = await offlineDB.customerLoyaltyCache.get(email)
    return row || null
}

// Lightweight refresh — just enough to keep the offline receipt-number
// predictor accurate. Unlike warmStaffSession (login-only, heavy), this is
// cheap enough to call every time the POS screen mounts/reconnects online,
// so the cached "latest receipt_number" for this branch never goes stale.
// Uses bulkPut (not clear+bulkPut) so it never wipes locally-queued,
// not-yet-synced orders that live in the same table.
export async function refreshOrdersCacheForReceiptNumbers(branchId) {
    if (!branchId) return
    try {
        const { data: orders, error } = await supabase
            .from('orders')
            .select('order_id, client_ref, reference_number, receipt_number, branch_id, order_status, created_at')
            .eq('branch_id', branchId)
            .not('receipt_number', 'is', null)
            .order('receipt_number', { ascending: false })
            .limit(20)

        if (error) throw error
        if (!orders?.length) return

        // Merge onto whatever's already cached for these order_ids, so we
        // don't clobber fuller rows (items/cashier_name etc.) written elsewhere.
        for (const o of orders) {
            const existing = await offlineDB.ordersCache.get(o.order_id)
            await offlineDB.ordersCache.put({ ...existing, ...o })
        }

        // Dedicated pointer for offline receipt numbering — independent of
        // ordersCache queries (which are fragile if branch_id types ever
        // mismatch). orders[0] is the highest receipt_number since we
        // ordered descending above.
        await setLastKnownReceiptNumber(branchId, orders[0].receipt_number)
    } catch (err) {
        console.error('Failed to refresh receipt-number cache:', err)
    }
}

// ── Offline receipt numbering pointer ───────────────────────────────
// Single source of truth for "what's the next receipt number to hand out
// while offline". Updated whenever we learn a real receipt number from the
// server (login prefetch, periodic refresh, or a successful online
// checkout), and advanced locally on every offline checkout.
export async function setLastKnownReceiptNumber(branchId, receiptNumber) {
    if (!branchId || !receiptNumber) return
    const key = `lastReceiptNumber:${branchId}`
    const existing = await offlineDB.metaCache.get(key)
    const digits = String(receiptNumber).match(/\d+/)
    if (!digits) return
    const num = parseInt(digits[0], 10)
    const existingNum = existing ? parseInt(String(existing.value.raw).match(/\d+/)?.[0] || '0', 10) : -Infinity
    // Never move the pointer backwards (e.g. a stale/partial refresh
    // shouldn't undo a number we've already handed out offline).
    if (num >= existingNum) {
        await offlineDB.metaCache.put({ key, value: { raw: String(receiptNumber), num } })
    }
}

export async function getNextOfflineReceiptNumberFromMeta(branchId) {
    const MIN_WIDTH = 6
    const key = `lastReceiptNumber:${branchId}`
    const cached = await offlineDB.metaCache.get(key)
    if (!cached?.value) return String(1).padStart(MIN_WIDTH, '0')

    const { raw, num } = cached.value
    const nextNum = num + 1
    const digits = String(raw).match(/\d+/)[0]
    const width = Math.max(digits.length, MIN_WIDTH)
    const prefix = raw.slice(0, raw.length - digits.length)
    const next = `${prefix}${String(nextNum).padStart(width, '0')}`

    // Advance the pointer immediately so a second offline checkout right
    // after this one continues from 82, not 81 again.
    await offlineDB.metaCache.put({ key, value: { raw: next, num: nextNum } })
    return next
}
// ── Loyalty Catalog (tiers, rewards, campaigns, achievements) — global, not branch-scoped ──
export async function cacheLoyaltyCatalog() {
    const { data: tiers } = await supabase
        .schema('marketing')
        .from('loyalty_tiers')
        .select(`tier_id, tier_name, tier_level, points_required, tier_icon,
            loyalty_tier_benefits ( benefit_description )`)
        .eq('is_archived', false)
        .order('tier_level', { ascending: true })

    await offlineDB.loyaltyTiersCache.clear()
    await offlineDB.loyaltyTiersCache.bulkPut((tiers || []).map((tier, index, arr) => ({
        id: tier.tier_id,
        name: tier.tier_name,
        tierLevel: tier.tier_level,
        minPoints: tier.points_required,
        maxPoints: index < arr.length - 1 ? arr[index + 1].points_required - 1 : null,
        benefits: (tier.loyalty_tier_benefits || []).map(b => b.benefit_description),
        image_url: tier.tier_icon || '',
    })))

    const { data: rewards } = await supabase
        .schema('marketing')
        .from('rewards')
        .select('*')
        .eq('is_archived', false)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

    await offlineDB.loyaltyCatalogRewardsCache.clear()
    await offlineDB.loyaltyCatalogRewardsCache.bulkPut((rewards || []).map(r => ({ id: r.reward_id, raw: r })))

    const { data: achievements } = await supabase
        .schema('marketing')
        .from('achievements')
        .select(`achievement_id, title, description, reward_points, badge_icon, status, is_repeatable,
            achievement_rules ( rule_id, condition_label, rule_type, target_value, timeframe_type )`)
        .eq('is_archived', false)
        .eq('status', 'active')
        .order('created_at', { ascending: false })

    await offlineDB.loyaltyCatalogAchievementsCache.clear()
    await offlineDB.loyaltyCatalogAchievementsCache.bulkPut((achievements || []).map(a => ({ id: a.achievement_id, raw: a })))

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const res = await fetch(`${API_BASE_URL}/api/loyalty-rewards/campaigns`, { headers: authHeaders() })
    if (res.ok) {
        const { campaigns } = await res.json()
        await offlineDB.loyaltyCatalogCampaignsCache.clear()
        await offlineDB.loyaltyCatalogCampaignsCache.bulkPut((campaigns || []).map(c => ({ id: c.promotion_id, raw: c })))
    }
}

export async function getLoyaltyTiersOffline() {
    return offlineDB.loyaltyTiersCache.orderBy('tierLevel').toArray()
}
export async function getLoyaltyCatalogRewardsOffline() {
    return (await offlineDB.loyaltyCatalogRewardsCache.toArray()).map(r => r.raw)
}
export async function getLoyaltyCatalogAchievementsOffline() {
    return (await offlineDB.loyaltyCatalogAchievementsCache.toArray()).map(r => r.raw)
}
export async function getLoyaltyCatalogCampaignsOffline() {
    return (await offlineDB.loyaltyCatalogCampaignsCache.toArray()).map(r => r.raw)
}

export async function cacheBranchBaristasAndSchedules(branchId) {
    if (!branchId) return

    const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/barista/branch/${branchId}/offline-cache`,
        { headers: authHeaders() }
    )
    if (!res.ok) {
        console.error('Failed to fetch baristas for offline cache:', res.status)
        return
    }
    const baristas = await res.json()

    await offlineDB.baristaCache.where('branch_id').equals(branchId).delete()
    await offlineDB.baristaCache.bulkPut(baristas || [])

    const baristaIds = (baristas || []).map(b => b.barista_id)
    if (baristaIds.length === 0) return

    const { data: schedules } = await supabase
        .from('staff_schedules')
        .select('barista_id, branch_id, work_days, default_scheduled_in, default_scheduled_out')
        .in('barista_id', baristaIds)

    await offlineDB.scheduleCache.bulkPut((schedules || []).map(s => ({
        owner_key: `barista:${s.barista_id}`, branch_id: branchId, ...s,
    })))

    const start = new Date(); start.setDate(1)
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0)
    const { data: attendance } = await supabase
        .from('staff_attendance')
        .select('*')
        .in('barista_id', baristaIds)
        .gte('date', start.toISOString().slice(0, 10))
        .lte('date', end.toISOString().slice(0, 10))

    await offlineDB.attendanceCache.bulkPut((attendance || []).map(a => ({
        ...a, owner_key: `barista:${a.barista_id}`,
    })))
}

export async function cacheAdminPinsForBranch(branchId) {
    if (!branchId) return

    const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/staff/branch/${branchId}/admin-pin-cache`,
        { headers: authHeaders() }
    )
    if (!res.ok) {
        console.error('Failed to fetch admin pins for offline cache:', res.status)
        return
    }
    const { pinHashes } = await res.json()
    await offlineDB.adminPinCache.put({ branch_id: branchId, pinHashes: pinHashes || [] })
}

// ── Offline: Staff Count ────────────────────────────────────────────
// Approximates the server's discrepancy check using the last cached stock
// snapshot (populated by InventoryManagement's online fetchBranchInventory).
// The real check re-runs server-side once syncAdjustmentRequest/syncStockCount syncs.
export async function submitStaffCountOffline({ branchId, ingredientId, countedQuantity, countType, staffRecordId, baristaId, unit }) {
    const cachedItem = await offlineDB.invStockListCache
        .where('branch_id').equals(branchId)
        .and(i => String(i.ingredient_id) === String(ingredientId))
        .first()
    const actual = Number(cachedItem?.current_stock || 0)
    const discrepancy = Number((countedQuantity - actual).toFixed(2))

    await enqueueAction('stock_count', {
        branchId, ingredientId, countedQuantity, countType,
        staffRecordId, baristaId, unit,
    })

    return { match: discrepancy === 0, discrepancy, counted: countedQuantity, actual }
}

// Which count types (beginning/end_of_shift) already have a *pending* offline
// submission today, for this ingredient — mirrors the online /today-types check.
export async function getPendingStockCountTypesOffline(branchId, ingredientId) {
    const today = new Date().toISOString().split('T')[0]
    const pending = await offlineDB.syncQueue.where('type').equals('stock_count').toArray()
    return pending
        .filter(a => a.payload.branchId === branchId
            && String(a.payload.ingredientId) === String(ingredientId)
            && a.created_at.startsWith(today))
        .map(a => a.payload.countType)
}

// ── Offline: Inventory Adjustment Requests ──────────────────────────
export async function submitAdjustmentRequestOffline({ branchId, ingredientId, adjustmentType, quantity, reason, notes, staffRecordId, baristaId }) {
    return enqueueAction('adjustment_request', {
        branchId, ingredientId, adjustmentType, quantity, reason, notes,
        staffRecordId, baristaId,
    })
}

// Not-yet-synced adjustment requests, shaped like the server's GET rows so
// they can be merged straight into the same table the UI already renders.
export async function getPendingAdjustmentRequestsOffline(branchId, inventoryList = []) {
    const pending = await offlineDB.syncQueue.where('type').equals('adjustment_request').toArray()
    return pending
        .filter(a => a.payload.branchId === branchId)
        .map(a => {
            const inv = inventoryList.find(i => String(i.ingredient_id) === String(a.payload.ingredientId))
            return {
                id: `local_${a.localId}`,
                localId: a.localId,
                ingredient_id: a.payload.ingredientId,
                ingredient_name: inv?.name || 'Unknown Ingredient',
                ingredient_unit: inv?.unit || '',
                adjustment_type: a.payload.adjustmentType,
                quantity: a.payload.quantity,
                reason: a.payload.reason,
                notes: a.payload.notes,
                status: 'pending',
                requested_by: 'You (offline)',
                created_at: a.created_at,
                isOffline: true,
            }
        })
}

export async function cancelPendingAdjustmentRequestOffline(localId) {
    await offlineDB.syncQueue.delete(localId)
}

export async function getBaristasOffline(branchId) {
    return offlineDB.baristaCache.where('branch_id').equals(branchId).toArray()
}

// ── Cash Drawer ─────────────────────────────────────────────
export async function getOpenCashDrawerSessionOffline(branchId) {
    const cached = await offlineDB.cashDrawerSessionCache.get(branchId)
    return cached && cached.status === 'open' ? cached : null
}

export async function getCashDrawerTransactionsOffline(sessionId) {
    return offlineDB.cashDrawerTransactionsCache
        .where('session_id').equals(sessionId)
        .reverse().sortBy('created_at')
}

// Call this from the ONLINE fetch path so offline reads stay fresh
export async function cacheCashDrawerSession(branchId, session) {
    if (!session) { await offlineDB.cashDrawerSessionCache.delete(branchId); return }
    await offlineDB.cashDrawerSessionCache.put({ ...session, branch_id: branchId })
}

export async function cacheCashDrawerTransactions(sessionId, transactions) {
    await offlineDB.cashDrawerTransactionsCache.where('session_id').equals(sessionId).delete()
    await offlineDB.cashDrawerTransactionsCache.bulkPut(transactions || [])
}

export async function openCashDrawerOffline({ branchId, baristaId, openingAmount, openingDenominations, notes }) {
    const alreadyOpen = await offlineDB.cashDrawerSessionCache.get(branchId)
    if (alreadyOpen?.status === 'open') {
        throw new Error('A cash drawer is already open for this branch on this device.')
    }
    const localSessionId = `local_session_${uuidv4()}`
    const session = {
        session_id: localSessionId,
        branch_id: branchId,
        barista_id: baristaId,
        status: 'open',
        opening_amount: openingAmount,
        opening_denominations: openingDenominations,
        notes: notes || null,
        opened_at: new Date().toISOString(),
    }
    await offlineDB.cashDrawerSessionCache.put(session)
    await enqueueAction('open_cash_drawer', { localSessionId, branchId, baristaId, openingAmount, openingDenominations, notes })
    return session
}

export async function logCashDrawerTransactionOffline({ sessionId, branchId, baristaId, type, amount, reason }) {
    const localTxId = `local_tx_${uuidv4()}`
    const transaction = {
        transaction_id: localTxId,
        session_id: sessionId,
        branch_id: branchId,
        barista_id: baristaId,
        type, amount, reason,
        created_at: new Date().toISOString(),
    }
    await offlineDB.cashDrawerTransactionsCache.put(transaction)
    await enqueueAction('cash_drawer_transaction', { localTxId, sessionId, branchId, baristaId, type, amount, reason })
    return transaction
}

export async function closeCashDrawerOffline({ sessionId, branchId, baristaId, closingAmount, closingDenominations, closingNotes, expectedAmount, variance }) {
    await enqueueAction('close_cash_drawer', { sessionId, branchId, baristaId, closingAmount, closingDenominations, closingNotes, expectedAmount, variance })

    // Preserve the full closed-session record in the logs cache BEFORE
    // clearing the "currently open" cache below — otherwise the closing
    // details (amount, denominations, variance, notes) are lost entirely
    // until this action syncs, since cashDrawerSessionCache only tracks
    // the currently-open drawer, not history.
    const existing = await offlineDB.cashDrawerSessionCache.get(branchId)
    if (existing) {
        let baristaInfo = existing.barista || null
        if (!baristaInfo && existing.barista_id) {
            const b = await offlineDB.baristaCache.get(existing.barista_id)
            if (b) baristaInfo = { first_name: b.first_name, last_name: b.last_name }
        }
        let closedByInfo = null
        if (baristaId) {
            const b = await offlineDB.baristaCache.get(baristaId)
            if (b) closedByInfo = { first_name: b.first_name, last_name: b.last_name }
        }

        await offlineDB.cashDrawerLogsCache.put({
            ...existing,
            branch_id: branchId,
            status: 'closed',
            closed_by_barista_id: baristaId,
            closing_amount: closingAmount,
            closing_denominations: closingDenominations,
            closing_notes: closingNotes,
            expected_amount: expectedAmount,
            variance,
            closed_at: new Date().toISOString(),
            barista: baristaInfo,
            closed_by: closedByInfo,
        })
    }

    await offlineDB.cashDrawerSessionCache.delete(branchId)
}

// ── Remake Requests ─────────────────────────────────────────
export async function getActiveRemakeRequestsOffline(branchId) {
    return offlineDB.remakeRequestsCache
        .where('branch_id').equals(branchId)
        .and(r => r.status === 'pending' || r.status === 'in_progress')
        .toArray()
}

export async function submitRemakeRequestOffline({
    branchId, orderId, referenceNumber, reason,
    responsibleStaff, remakeItems, submittedBy, submittedByBarista,
}) {
    const localRequestId = `local_remake_${uuidv4()}`
    const now = new Date().toISOString()
    const request = {
        remake_request_id: localRequestId, order_id: orderId, reference_number: referenceNumber,
        branch_id: branchId, reason, responsible_staff: responsibleStaff, remake_items: remakeItems,
        submitted_by: submittedBy, submitted_by_barista: submittedByBarista,
        status: 'pending', claimed_by: null, created_at: now,
    }
    await offlineDB.remakeRequestsCache.put(request)

    // Pre-shaped like the online join, so RemakeActivityCard can show it right away
    for (const staff of responsibleStaff) {
        await offlineDB.remakeActivityLogsCache.put({
            activity_id: `local_activity_${uuidv4()}`, barista_id: staff.staff_id, branch_id: branchId,
            activity_type: 'remake_request', order_id: orderId, reference_number: referenceNumber,
            reason, remake_request_id: localRequestId, created_at: now, remake_requests: request,
        })
    }

    await enqueueAction('create_remake_request', {
        localRequestId, branchId, orderId, referenceNumber, reason,
        responsibleStaff, remakeItems, submittedBy, submittedByBarista,
    })
    window.dispatchEvent(new CustomEvent('kaperk:remake-request-changed'))
    return request
}

export async function claimRemakeRequestOffline(remakeRequestId, claimantId) {
    const existing = await offlineDB.remakeRequestsCache.get(remakeRequestId)
    if (!existing) throw new Error('This remake request is not cached offline yet.')
    if (existing.status === 'in_progress' && existing.claimed_by !== claimantId) {
        return { conflict: true, request: existing }
    }
    const updated = { ...existing, status: 'in_progress', claimed_by: claimantId }
    await offlineDB.remakeRequestsCache.put(updated)
    await enqueueAction('claim_remake_request', { remakeRequestId, claimantId })
    window.dispatchEvent(new CustomEvent('kaperk:remake-request-changed'))
    return { conflict: false, request: updated }
}

export async function releaseRemakeRequestOffline(remakeRequestId) {
    const existing = await offlineDB.remakeRequestsCache.get(remakeRequestId)
    if (!existing) return
    await offlineDB.remakeRequestsCache.put({ ...existing, status: 'pending', claimed_by: null })
    await enqueueAction('release_remake_request', { remakeRequestId })
    window.dispatchEvent(new CustomEvent('kaperk:remake-request-changed'))
}

export async function completeRemakeRequestOffline(remakeRequestId) {
    const existing = await offlineDB.remakeRequestsCache.get(remakeRequestId)
    if (existing) {
        await offlineDB.remakeRequestsCache.put({ ...existing, status: 'completed', completed_at: new Date().toISOString() })
    }
    await enqueueAction('complete_remake_request', { remakeRequestId })
    window.dispatchEvent(new CustomEvent('kaperk:remake-request-changed'))
}